import _ from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';
import Avatar from '../user/Avatar';
import { modal } from '../modal/ModalEmitter';
import ApiService from '../../services/ApiService';
import SidebarSettings from './SidebarSettings';
import { whichMenuItem, menuDefs } from '../../util/page';
// import AddNote from '../calendar/AddNote';
import ViewNotes from '../calendar/ViewNotes';
import SalesMenuList from './SalesMenuList';
// import EventForm from '../calendar/EventForm';

const hasListItem = location => {
  const regex = /^\/(?:list|db)\/(.*)/;
  const matches = location.match(regex);
  if (!matches) {
    return [];
  }

  return matches[1].split('/');
};

const MenuList = ({ closeMenu, toggleSideBar }) => {
  const [fetchingAttachments, setFetchingAttachments] = useState(false);
  const [attachments, setAttachments] = useState(0);

  const [notes, setNotes] = useState(0);
  const [fetchingNotes, setFetchingNotes] = useState(false);
  // const [fetchingEvents, setFetchingEvents] = useState(false);
  const [events, setEvents] = useState([]);

  const config = useSelector(state => state.app.config);
  const { me } = useSelector(state => state.chat);
  const { user } = useSelector(state => state.app);
  const branchOptions = useSelector(state => state.app.branchOptions);
  const filteredTree = useSelector(state => state.app.filteredTree);

  const page = useSelector(state => state.page);
  const location = useLocation();
  const history = useHistory();
  const loggedIn = !!user.username;
  const branchedHere = whichMenuItem(location.pathname);
  const [listName, listItem] = hasListItem(decodeURIComponent(location.pathname));

  // TODO Our page config structure probably could be simplified
  const pageConfig = _.get(page, 'data.page');
  const baseMenu = ['accounting', 'leasing', 'equipment', 'parts', 'service', 'system', 'solutions'];

  const hasOption = option => {
    if (!pageConfig || !pageConfig.ValidTypes || !Array.isArray(pageConfig.ValidTypes)) {
      return false;
    }

    return pageConfig.ValidTypes.includes(option);
  };

  const isAllowed = (item = '') => {
    for (const tree of filteredTree) {
      if (tree.name && tree.name === item && tree.menu.length) {
        return true;
      }
    }

    return false;
  };

  const canHaveAttachments = listName && listItem !== 'new' && hasOption('upload');
  const canHaveEvents = listName && listItem !== 'new' && /^SMM/.test(listName);
  const canHaveNotes = listName && listItem !== 'new' && hasOption('notes');

  const showSettings = () => {
    modal.open('sidebarSettings', {
      title: 'Sidebar Settings',
      size: 'md',
      style: 'center',
      hidePrint: true,
      hideFooter: true,
      headerClass: 'primary',
      message: <SidebarSettings toggleSideBar={toggleSideBar} />,
    });
  };

  useEffect(() => {
    setEvents([]);
    if (canHaveAttachments) {
      setFetchingAttachments(true);
      ApiService.get(`list/${listName}/item/${encodeURIComponent(listItem)}/attachments`)
        .then(response => {
          setAttachments(response.data.length);
        })
        .finally(() => {
          setFetchingAttachments(false);
        });
    }

    if (canHaveEvents) {
      ApiService.get('events', { params: { repairOrder: listItem } }).then(response => {
        setEvents(response.data || []);
      });
    }

    if (canHaveNotes) {
      setFetchingNotes(true);
      ApiService.get('notes', { params: { listId: listName, itemId: listItem } })
        .then(response => {
          setNotes(response.data.length);
        })
        .finally(() => {
          setFetchingNotes(false);
        });
    }
  }, [location.pathname, pageConfig]);

  const renderCalendarButton = () => {
    const buttons = [];
    const hasEvents = !!(canHaveEvents && events.length);

    const primary = (
      <Link
        key="cal"
        className={classNames('menu-item', 'Calendar', branchedHere === 'calendar' && 'highlighted')}
        to="/SDC0"
        title="Calendar"
        onClick={() => closeMenu()}
      >
        <div className="menu-item-content">
          <i className="fas fa-calendar fa-fw" />
          <span className="menu-label">Calendar</span>
          {hasEvents && (
            <Badge pill variant="primary">
              {events.length}
            </Badge>
          )}
        </div>
      </Link>
    );

    buttons.push(primary);

    if (hasEvents) {
      const firstEvent = events[0];
      const startDate = moment(firstEvent.start_date, 'YYYY-MM-DD HH:mm:ss a');
      const endDate = moment(firstEvent.end_date, 'YYYY-MM-DD HH:mm:ss a');

      buttons.push(
        <div
          key={`event-${firstEvent.id}`}
          className={classNames('menu-item', 'event-item')}
          title={firstEvent.text}
          onClick={() => {
            history.push(`/SDC0/${firstEvent.id}`);
            // TODO Can't do this yet b/c it's tied to cal.js crap
            // modal.open('eventForm', {
            //   id: event.eventId,
            //   title: event.eventTitle,
            //   message: (
            //     <EventForm
            //       eventId={event.eventId}
            //       form={event}
            //       close={modal.close}
            //     />
            //   ),
            // });
          }}
        >
          <div className="menu-item-content">
            <span className="event-caption mini-only">
              <div className="month">{startDate.format('MMM')}</div>
              <div className="day">{startDate.format('DD')}</div>
            </span>
            <div className="menu-label">
              <div className="event-date">
                {startDate.format('MMM DD h:mm a')} - {endDate.format('MMM DD h:mm a')}
              </div>
              <div className="event-title">
                <div className="label" style={{ backgroundColor: firstEvent.color, color: firstEvent.textColor }}>
                  {firstEvent.templateName}
                </div>
                {firstEvent.text}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return buttons;
  };

  const renderFavoritesButton = () => {
    const label = 'Favorites';
    const className = classNames('menu-item', label, {
      highlighted: branchedHere === 'favorites',
    });

    return (
      <Link key="favorites" className={className} to="/Favorites" title={label} onClick={() => closeMenu()}>
        <div className="menu-item-content">
          <i className="fas fa-star fa-fw" />
          <span className="menu-label">{label}</span>
        </div>
      </Link>
    );
  };

  const renderUserBadge = () => {
    if (!loggedIn) {
      return (
        <Link className="menu-item" to="/login">
          <i className="fas fa-account fa-fw" /> Login
        </Link>
      );
    }

    return (
      <div className="user-details">
        <div
          className="user-settings expanded never-mobile"
          title="Sidebar Settings"
          onClick={() => {
            showSettings();
          }}
        >
          <i className="fas fa-cog fa-fw" />
        </div>
        <div
          className="user-branch expanded"
          title="Change Branch"
          onClick={() => {
            modal.open('changeBranch', { branches: branchOptions });
          }}
        >
          <i className="fas fa-code-branch fa-fw" />
        </div>
        <Avatar
          width={30}
          user={{ avatar: user.avatar }}
          status={me.status}
          onClick={() => {
            history.push('/profile');
          }}
        />
        <div className="user-name expanded">{`${user.firstName} ${user.lastName}`}</div>
      </div>
    );
  };

  const renderBaseMenu = () => {
    return baseMenu.map(item => {
      const menuItem = menuDefs[item];
      const link = menuItem.link || menuItem.label;
      const className = classNames('menu-item', {
        [menuItem.label.replace(/\s/g, '')]: true,
        highlighted: branchedHere === item,
        hidden: !isAllowed(link),
      });

      if (item === 'equipment' && !config.features.equipment) {
        return null;
      }

      return (
        <Link
          key={menuItem.label}
          className={className}
          to={`/menu/${link}`}
          title={menuItem.label}
          onClick={() => closeMenu()}
        >
          <div className="menu-item-content">
            <i className={`fas fa-${menuItem.icon} fa-fw`} />
            <span className="menu-label">{menuItem.label}</span>
          </div>
        </Link>
      );
    });
  };

  return (
    <div className="menu-list">
      {renderUserBadge()}
      <div
        className="menu-item mini-only"
        title="Change Branch"
        onClick={() => {
          modal.open('changeBranch', { branches: branchOptions });
        }}
      >
        <div className="menu-item-content">
          <i className="fas fa-fw fa-code-branch" />
        </div>
      </div>
      <Link
        key="home"
        className={classNames('menu-item', 'Home', branchedHere === 'home' && 'highlighted')}
        to="/"
        title="Dashboard"
        onClick={() => closeMenu()}
      >
        <div className="menu-item-content">
          <i className="fas fa-home fa-fw" />
          <span className="menu-label">Home</span>
        </div>
      </Link>
      {renderCalendarButton()}
      {renderFavoritesButton()}
      {branchedHere !== 'sales' ? renderBaseMenu() : <SalesMenuList pageName={page.pageName} />}
      {canHaveAttachments && (
        <div
          className="menu-item attachments"
          title="Attachments"
          onClick={() => {
            modal.open('attachments', {
              itemId: listItem,
              itemName: listName,
              onChange: items => setAttachments(Array.isArray(items) ? items.length : 0),
            });
          }}
        >
          <div className="menu-item-content">
            <i className="fas fa-fw fa-paperclip" />
            <span className="menu-label">Attachments</span>
          </div>
          {fetchingAttachments ? (
            <span className="loader">
              <ClipLoader
                size={10}
                css={{ borderColor: 'var(--primary-fore) var(--primary-fore) transparent var(--primary-fore)' }}
                color="#fff"
              />
            </span>
          ) : (
            <>
              {attachments !== 0 ? (
                <Badge pill variant="primary">
                  {attachments}
                </Badge>
              ) : (
                <Badge pill variant="success">
                  +
                </Badge>
              )}
            </>
          )}
        </div>
      )}
      {canHaveNotes && (
        <div
          className="menu-item notes"
          title="Notes"
          onClick={() => {
            modal.open('viewNotes', {
              title: 'View Notes',
              style: 'center',
              size: 'md',
              hideFooter: true,
              hidePrint: true,
              message: <ViewNotes listItem={listItem} listName={listName} />,
            });
          }}
        >
          <div className="menu-item-content">
            <i className="fas fa-sticky-note fa-fw" />
            <span className="menu-label">Notes</span>
          </div>
          {fetchingNotes ? (
            <span className="loader">
              <ClipLoader
                size={10}
                css={{ borderColor: 'var(--primary-fore) var(--primary-fore) transparent var(--primary-fore)' }}
                color="#fff"
              />
            </span>
          ) : (
            <>
              {notes !== 0 ? (
                <Badge pill variant="primary">
                  {notes}
                </Badge>
              ) : (
                <Badge pill variant="success">
                  +
                </Badge>
              )}
            </>
          )}
        </div>
      )}
      <div className="bottom">
        <div
          className="menu-item mini-only"
          title="Sidebar Settings"
          onClick={() => {
            showSettings();
          }}
        >
          <div className="menu-item-content">
            <i className="fas fa-cog fa-fw" />
          </div>
        </div>
      </div>
    </div>
  );
};

MenuList.propTypes = {
  closeMenu: PropTypes.func,
  toggleSideBar: PropTypes.func,
};

export default MenuList;
